import { Info } from "lucide-react";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgChip } from "@intelligems/ig-theme/IgChip";
import { IgDialog } from "@intelligems/ig-theme/IgDialog";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import Stack from "@mui/material/Stack";

import { StepTimeline } from "@src/components/steps/StepTimeline";
import { useDialog } from "@src/hooks/dialog";
import { useSelector } from "@src/store";
import { getTestBuild } from "@src/store/slices/testBuilds";
import { cancelTestBuild } from "@src/store/slices/testBuilds/runTestBuild";
import type { TestBuild } from "@src/store/slices/testBuilds/types";

/**
 * The build in front of the merchant while it runs, wherever it was started.
 * `runTestBuild` opens it and closes it once the build is done; a build that
 * failed stays open on the step that stopped it.
 */
export const TestBuildDialog = () => {
  const { isOpen, close, props } = useDialog("testBuild");
  const build = useSelector((state) =>
    props ? getTestBuild(state, props.buildId) : null
  );
  // The cross stops a running build; on a failed one it only closes.
  const onClose = () => {
    if (build?.status === "running") {
      cancelTestBuild(build.id);
    }
    close();
  };
  return <TestBuildDialogView open={isOpen} build={build} onClose={onClose} />;
};

/**
 * The dialog's drawing, from the build alone. The header reads as the
 * recommendation detail's: the test-type chip over the test's name, under a
 * small caps label.
 */
export const TestBuildDialogView = ({
  open,
  build,
  onClose,
}: {
  open: boolean;
  build: TestBuild | null;
  onClose: () => void;
}) => {
  const failed = build?.status === "failed";

  return (
    <IgDialog
      showDialog={open}
      // A stray click beside a running build must not cancel it.
      handleClose={(reason) => {
        if (reason !== "backdropClick" || failed) {
          onClose();
        }
      }}
      showCloseIcon
      title={null}
      hideActions
      maxWidth="sm"
      minWidth="560px"
      // Its height follows the steps, so nothing floats in empty space.
      dialogHeight={null}
      // Wider than the corners' 32px, so the text never crowds them.
      padding="48px"
      // Figma's audit modal (780:7377) rounds its corners at 32px.
      sx={{ "& .MuiDialog-paper": { borderRadius: "32px" } }}
      dontUseDialogContent
      rawContent={
        // 32px between sections, the audit modal's `space-8`.
        <Stack spacing={8}>
          <Stack spacing={3}>
            <IgTypography
              variant="body3"
              color="text.tertiary"
              sx={{
                fontWeight: 600,
                letterSpacing: 0.8,
                textTransform: "uppercase",
              }}
            >
              Building your test
            </IgTypography>
            {build?.typeLabel && (
              <IgChip
                label={build.typeLabel}
                size="small"
                sx={{ alignSelf: "flex-start" }}
              />
            )}
            {build?.name && (
              <IgTypography variant="h2">{build.name}</IgTypography>
            )}
          </Stack>
          {build && <StepTimeline steps={build.steps} gap={20} />}
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Info size={16} className="mt-0.5 shrink-0 text-muted-foreground" />
            <IgTypography variant="body2" color="text.secondary">
              {failed
                ? "The build stopped at the marked step. Close this and try again."
                : "It opens as a draft. Nothing goes live until you save and start it."}
            </IgTypography>
          </Stack>
          {failed && (
            <IgButton
              variant="contained"
              onClick={onClose}
              sx={{ alignSelf: "flex-end" }}
            >
              Close
            </IgButton>
          )}
        </Stack>
      }
    />
  );
};
